/**
 * sendungslage.ts — leitet aus den DHL-Rohdaten des letzten Events eine
 * verstaendliche Sendungslage fuer die Karte ab (24.08.2026).
 *
 * Hintergrund: das interne 7er-Enum (internal_status) sagt bei den wichtigsten
 * Problemfaellen nur "Unbekannt" — DHLs eigener Beschreibungstext sagt dagegen
 * exakt was los ist ("Es erfolgte keine Einlieferung ... storniert", "liegt in
 * der Filiale zur Abholung bereit"). Diese Ableitung ist REIN ANZEIGE:
 * internal_status wird nirgends umgedeutet (steuert Auto-Done/Alarme/Zaehler,
 * siehe Programm-CLAUDE.md "internalStatus NICHT umdeuten").
 *
 * Pure functions — getestet in sendungslage.test.ts.
 */

export interface SendungslageBadge {
  label: string;
  /** Tailwind-Farbton der Karte: red = handeln, orange = kommt zurueck, blue = Kunde muss abholen, yellow = beobachten */
  tone: 'red' | 'orange' | 'blue' | 'yellow';
}

/** Entfernt HTML-Tags (DHL-Texte enthalten z.T. <a href=...>-Links) und kuerzt. */
export function cleanEventText(
  description: string | null | undefined,
  maxLen = 160,
): string | null {
  if (!description) return null;
  const text = description.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!text) return null;
  return text.length > maxLen ? `${text.slice(0, maxLen - 1)}…` : text;
}

/**
 * Erkennt aus (Code, Beschreibung, ruecksendung-Flag) die handlungsrelevanten
 * Sonderfaelle. Liefert null wenn kein Sonderfall — dann reicht das normale
 * Status-Label. Reihenfolge = Prioritaet (Verlust vor Ruecksendung vor Rest).
 */
export function deriveSendungslageBadge(
  code: string | null | undefined,
  description: string | null | undefined,
  istRuecksendung: boolean | null | undefined,
): SendungslageBadge | null {
  const desc = (description ?? '').toLowerCase();
  const c = (code ?? '').toUpperCase();

  // Verlust/Zerstoerung — DHL entsorgt beschaedigte Sendungen ("Der Inhalt der
  // Sendung fehlt", "wurde daher entsorgt"). Muss VOR allem anderen kommen.
  if (/entsorgt|vernichtet|inhalt[^.]*fehlt|verloren\s+gegangen/.test(desc)) {
    return { label: 'Sendung entsorgt/verloren — Ersatz klären', tone: 'red' };
  }

  // Geistersendung: Label erzeugt, Paket nie bei DHL eingeliefert, DHL hat die
  // Sendungsdaten storniert. Haeufigste "Unbekannt"-Ursache (19 offene Faelle
  // am 24.08.). Ware ging i.d.R. mit anderem Label raus — pruefen + schliessen.
  if (/keine einlieferung/.test(desc) && /storniert/.test(desc)) {
    return { label: 'Nie eingeliefert — DHL hat Sendungsdaten storniert', tone: 'red' };
  }

  // DHLs hartes Ruecksendungs-Flag: Paket laeuft zu uns zurueck (oder ist schon
  // da, wenn der letzte Event "zugestellt" ist — Zustellort = unser Lager).
  if (istRuecksendung === true) {
    const zugestellt = c === 'ZU' || /erfolgreich zugestellt/.test(desc);
    return zugestellt
      ? { label: 'Rücksendung — Paket ist zurück bei uns', tone: 'orange' }
      : { label: 'Rücksendung an uns unterwegs', tone: 'orange' };
  }

  // Expliziter Ruecklauf-Text ohne Flag (Altdaten): Lagerfrist abgelaufen /
  // Annahme verweigert → Paket kommt zurueck.
  if (/zurückgesandt|zurück zum absender|annahme .*verweigert/.test(desc)) {
    return { label: 'Geht zurück an uns (nicht abgeholt/verweigert)', tone: 'orange' };
  }

  // Liegt abholbereit in Filiale/Packstation — Lagerfrist (7 Tage) tickt.
  if (c === 'LA' || /zur abholung bereit|liegt (ab sofort )?in der/.test(desc)) {
    return { label: 'Liegt zur Abholung bereit — Lagerfrist läuft', tone: 'blue' };
  }

  // Wird in Filiale gebracht (Zustellung nicht moeglich).
  if (c === 'ZF') {
    return { label: 'Wird in Filiale gebracht', tone: 'blue' };
  }

  // Zustellversuch erfolglos — meist harmlos (2. Versuch folgt), beobachten.
  if (c === 'ZN') {
    return { label: 'Zustellversuch erfolglos', tone: 'yellow' };
  }

  return null;
}

/** Tailwind-Klassen je tone — zentral damit die Karte konsistent faerbt. */
export const SENDUNGSLAGE_TONE_CLS: Record<SendungslageBadge['tone'], string> = {
  red: 'bg-red-50 text-red-700',
  orange: 'bg-orange-50 text-orange-700',
  blue: 'bg-blue-50 text-blue-700',
  yellow: 'bg-yellow-50 text-yellow-800',
};
